'use client';

import { useState } from 'react';
import { Code, ImageSquare, Play, Warning } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import type { BlindGenerationView } from '@/lib/arena/types';
import { posterUrlFor, htmlSourceFor, formatCredits, formatLatency, formatUsd } from './constants';

/**
 * The media surface for one generation card, shared by the detail grid and the
 * blind review canvas. It NEVER reads model/provider/label — it renders purely
 * from artifactType + artifact/extra URLs, so it is blind-safe by construction.
 *
 * - image     → <img>, click to zoom (fullscreen overlay).
 * - video     → inline <video>, muted autoplay on focus/hover, click toggles sound.
 * - html/viz  → screenshot poster with a code toggle (code only when html present).
 */
export function GenerationMedia({
  gen,
  className,
  onZoom,
}: {
  gen: BlindGenerationView;
  className?: string;
  onZoom?: (url: string) => void;
}) {
  const [showCode, setShowCode] = useState(false);
  const [unmuted, setUnmuted] = useState(false);
  const poster = posterUrlFor(gen);
  const html = htmlSourceFor(gen);
  const type = gen.artifactType;

  if (gen.status === 'failed') {
    return (
      <div className={cn('flex aspect-video w-full items-center justify-center rounded-lg border border-red-500/25 bg-red-500/[0.04]', className)}>
        <div className="flex flex-col items-center gap-1 text-red-300">
          <Warning weight="duotone" className="size-6" />
          <span className="text-[11px] font-medium">Generation failed</span>
        </div>
      </div>
    );
  }

  if (!poster && !html && !gen.artifactUrl) {
    return (
      <div className={cn('flex aspect-video w-full items-center justify-center rounded-lg border border-border/60 bg-muted/40', className)}>
        <ImageSquare weight="duotone" className="size-6 text-muted-foreground" />
      </div>
    );
  }

  // Video artifact — inline player.
  if (type === 'video') {
    return (
      <div className={cn('relative overflow-hidden rounded-lg border border-border/60 bg-black', className)}>
        <video
          src={gen.artifactUrl ?? undefined}
          poster={poster ?? undefined}
          muted={!unmuted}
          loop
          playsInline
          autoPlay
          onClick={(e) => {
            e.stopPropagation();
            setUnmuted((u) => !u);
            const el = e.currentTarget;
            el.muted = unmuted; // toggling
            void el.play().catch(() => {});
          }}
          className="aspect-video w-full cursor-pointer object-contain"
          data-testid="arena-video"
        />
        <span className="pointer-events-none absolute bottom-1 right-1 rounded bg-black/60 px-1 py-0.5 text-[9px] text-white">
          {unmuted ? 'sound on' : 'tap for sound'}
        </span>
      </div>
    );
  }

  // HTML/viz — screenshot poster + code toggle.
  if (type === 'html' || type === 'screenshot' || html) {
    return (
      <div className={cn('overflow-hidden rounded-lg border border-border/60 bg-muted/30', className)}>
        {showCode && html ? (
          <pre className="aspect-video w-full overflow-auto bg-zinc-950 p-2 text-[10px] leading-tight text-zinc-200" data-testid="arena-html-code">
            <code>{html}</code>
          </pre>
        ) : poster ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={poster}
            alt="Generated visualization"
            onClick={(e) => {
              e.stopPropagation();
              onZoom?.(poster);
            }}
            className="aspect-video w-full cursor-zoom-in object-contain"
            data-testid="arena-screenshot"
          />
        ) : (
          <div className="flex aspect-video w-full items-center justify-center">
            <Code weight="duotone" className="size-6 text-muted-foreground" />
          </div>
        )}
        {html && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowCode((s) => !s);
            }}
            className="flex w-full items-center justify-center gap-1 border-t border-border/60 bg-background/60 py-1 text-[10px] font-medium text-muted-foreground hover:text-foreground"
            data-testid="arena-code-toggle"
          >
            {showCode ? <ImageSquare weight="bold" className="size-3" /> : <Code weight="bold" className="size-3" />}
            {showCode ? 'Show render' : 'Show code'}
          </button>
        )}
      </div>
    );
  }

  // Image (default).
  const imgUrl = gen.artifactUrl ?? poster;
  return (
    <div className={cn('overflow-hidden rounded-lg border border-border/60 bg-muted/30', className)}>
      {imgUrl ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={imgUrl}
          alt="Generated image"
          onClick={(e) => {
            e.stopPropagation();
            onZoom?.(imgUrl);
          }}
          className="aspect-square w-full cursor-zoom-in object-contain"
          data-testid="arena-image"
        />
      ) : (
        <div className="flex aspect-square w-full items-center justify-center">
          <Play weight="duotone" className="size-6 text-muted-foreground" />
        </div>
      )}
    </div>
  );
}

/** Cost + latency footer, shown on EVERY card per plan B5. */
export function CardMetrics({
  gen,
  className,
}: {
  gen: Pick<BlindGenerationView, 'costCredits' | 'costUsd' | 'latencyMs'>;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center gap-2 text-[10px] text-muted-foreground', className)}>
      <span title="cost (credits)">{formatCredits(gen.costCredits)}</span>
      <span className="text-border">·</span>
      <span title="cost (usd)">{formatUsd(gen.costUsd)}</span>
      <span className="text-border">·</span>
      <span title="latency">{formatLatency(gen.latencyMs)}</span>
    </div>
  );
}

/** Fullscreen zoom overlay for images/screenshots. */
export function ZoomOverlay({ url, onClose }: { url: string | null; onClose: () => void }) {
  if (!url) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={url} alt="Zoomed" className="max-h-full max-w-full rounded-lg object-contain" />
    </div>
  );
}
